import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ConfigServiceProvider} from '../../../core/services/config-service/config-service';
import {StorageServiceProvider} from '../../../core/services/storage-service/storage-service';
import {Observable} from "rxjs/Observable";
import {News, NewsAttrs} from "../../model/news.model";
import {map} from "rxjs/operators";
import {TimeUtils} from "../../../../utils/time.utils";
import {NewsCreateDTO} from "../../model/news-create-dto.model";
import {Page} from "../../../core/model/page.model";
import {NewsComment} from "../../model/news-comment.model";
import {NewsWithComment} from "../../model/news-with-comment.model";
import {DbEntry} from '../../../core/model/db-entry.model';
import {from} from "rxjs/observable/from";

@Injectable()
export class NewsService {
  constructor(
    private http: HttpClient,
    private configService: ConfigServiceProvider,
    private storageService: StorageServiceProvider
  ) { }

  public load(): Observable<string> {
    return from(this.http.get(this.configService.getUrl('news'))
      .toPromise()
      .then((page: Page<NewsAttrs>) => this.saveNewsToStorage(JSON.stringify(page.content))));
  }

  private saveNewsToStorage(newsAttrsString: string): Promise<any> {
    const dbEntry: DbEntry = {
      lastModTime: TimeUtils.getUnixTimestamp(),
      value: newsAttrsString
    };
    return this.storageService.setObject('news', dbEntry);
  }

  getNews(): Observable<News[]> {
    return Observable.from(
      this.storageService.getOrFetch('news', this.load.bind(this), 60 * 60 * 8)
    ).pipe(
      map((newsAttrsArrayJSON: string) => {
        const newsAttrsArray = JSON.parse(newsAttrsArrayJSON);
        return (newsAttrsArray as NewsAttrs[]).map(this.mapNewsAttrsToNews)}),
    );
  }

  private mapNewsAttrsToNews(newsAttrs: NewsAttrs): News {
    return {
      ...newsAttrs,
      date: new Date(newsAttrs.date)
    }
  };

  like(id: number): Observable<void> {
    return this.http.get<void>(this.configService.getUrl('news') + '/' + id + '/like');
  }

  comment(id: number, text: string): Observable<void> {
    const requestData = new HttpParams().set('comment', text);
    const httpOptions = {
      headers: new HttpHeaders().set('Content-type', 'application/x-www-form-urlencoded; charset=utf-8')
    };

    return this.http.post<void>(this.configService.getUrl('news') + '/' + id + '/comment',
      requestData,
      httpOptions
    );
  }

  deleteComment(newsId: number, commentId: number): Observable<void> {
    return this.http.get<void>(
      this.configService.getUrl('news') + '/' + newsId + '/comment/' + commentId + '/delete'
    );
  }

  getComments(id: number): Observable<NewsComment[]> {
    return this.http.get<NewsWithComment>(
      this.configService.getUrl('news') + '/' + id + '/comments'
    ).pipe(
      map(nwcs => nwcs.comments)
    );
  }

  sendQuickNews(title: string, content: string): Observable<void> {
    const requestData = new HttpParams().set('title', title).set('content', content);
    const httpOptions = {
      'headers': new HttpHeaders().set('Content-type', 'application/x-www-form-urlencoded; charset=utf-8')
    };

    return this.http.post<void>(this.configService.getUrl('newsQuick'), requestData, httpOptions);
  }

  sendNews(news: NewsCreateDTO): Observable<void> {
    let formData = new FormData();
    let requestParams;

    requestParams = new HttpParams()
      .set('title', news.title)
      .set('content', news.content)
      .set('likeable', 'true')
      .set('commentable', 'true');

    if (news.images.length > 0) {
      formData.set('image', news.images[0].file);
    }
    news.files.forEach(file => formData.append('files', file.file, file.name));

    if (news.groups && news.groups.length > 0) {
      requestParams = requestParams.set('groups', JSON.stringify(news.groups.map(group => [group])));
    }

    return this.http.post<void>(this.configService.getUrl('news'), formData, {
      params: requestParams
    });
  }
}



// WEBPACK FOOTER //
// ./src/modules/news/services/news-service/news-service.ts